import { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";

import PublicLayout from "@/pages/public/PublicLayout";
import Home from "@/pages/public/Home";
import { InfoPage } from "@/pages/public/InfoPage";
import GlobalWorkforce from "@/pages/public/GlobalWorkforce";
import Contact from "@/pages/public/Contact";

import Login from "@/pages/auth/Login";
import Register from "@/pages/auth/Register";
import ForgotPassword from "@/pages/auth/ForgotPassword";
import ResetPassword from "@/pages/auth/ResetPassword";

import AdminLayout from "@/pages/admin/AdminLayout";
import Dashboard from "@/pages/admin/Dashboard";
import GlobalOps from "@/pages/admin/GlobalOps";
import Workers from "@/pages/admin/Workers";
import Teams from "@/pages/admin/Teams";
import Locations from "@/pages/admin/Locations";
import Campaigns from "@/pages/admin/Campaigns";
import Tasks from "@/pages/admin/Tasks";
import Leads from "@/pages/admin/Leads";
import Sales from "@/pages/admin/Sales";
import Performance from "@/pages/admin/Performance";
import Payments from "@/pages/admin/Payments";
import Reports from "@/pages/admin/Reports";
import AIOps from "@/pages/admin/AIOps";
import AdminNotifications from "@/pages/admin/AdminNotifications";
import AuditLogs from "@/pages/admin/AuditLogs";
import Settings from "@/pages/admin/Settings";

import WorkerLayout from "@/pages/worker/WorkerLayout";
import WorkerHome from "@/pages/worker/WorkerHome";
import WorkerTasks from "@/pages/worker/WorkerTasks";
import WorkerLeads from "@/pages/worker/WorkerLeads";
import WorkerPerformance from "@/pages/worker/WorkerPerformance";
import WorkerProfile from "@/pages/worker/WorkerProfile";

const ADMIN_ROLES = ["super_admin", "country_admin", "regional_manager", "city_manager", "team_leader"];

function Splash() {
  return (
    <div className="min-h-screen grid place-items-center bg-[#0B0F19]">
      <div className="flex flex-col items-center gap-4">
        <div className="h-12 w-12 rounded-xl bg-[#1D4ED8] grid place-items-center font-head font-extrabold text-white animate-pulse">GT</div>
        <p className="text-slate-400 text-sm">Loading GTForce…</p>
      </div>
    </div>
  );
}

function Protected({ children, roles }) {
  const { user, loading } = useAuth();
  if (loading || user === null) return <Splash />;
  if (!user) return <Navigate to="/login" replace />;
  if (roles && !roles.includes(user.role)) {
    return <Navigate to={user.role === "worker" ? "/app" : "/admin"} replace />;
  }
  return children;
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <Toaster position="top-right" richColors />
          <Routes>
            <Route element={<PublicLayout />}>
              <Route path="/" element={<Home />} />
              <Route path="/about" element={<InfoPage slug="about" />} />
              <Route path="/how-it-works" element={<InfoPage slug="how-it-works" />} />
              <Route path="/for-workers" element={<InfoPage slug="for-workers" />} />
              <Route path="/for-businesses" element={<InfoPage slug="for-businesses" />} />
              <Route path="/opportunities" element={<InfoPage slug="opportunities" />} />
              <Route path="/faq" element={<InfoPage slug="faq" />} />
              <Route path="/global-workforce" element={<GlobalWorkforce />} />
              <Route path="/contact" element={<Contact />} />
            </Route>

            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />

            <Route path="/admin" element={<Protected roles={ADMIN_ROLES}><AdminLayout /></Protected>}>
              <Route index element={<Dashboard />} />
              <Route path="global-operations" element={<GlobalOps />} />
              <Route path="workers" element={<Workers />} />
              <Route path="teams" element={<Teams />} />
              <Route path="locations" element={<Locations />} />
              <Route path="campaigns" element={<Campaigns />} />
              <Route path="tasks" element={<Tasks />} />
              <Route path="leads" element={<Leads />} />
              <Route path="sales" element={<Sales />} />
              <Route path="performance" element={<Performance />} />
              <Route path="payments" element={<Payments />} />
              <Route path="reports" element={<Reports />} />
              <Route path="ai-operations" element={<AIOps />} />
              <Route path="notifications" element={<AdminNotifications />} />
              <Route path="audit-logs" element={<AuditLogs />} />
              <Route path="settings" element={<Settings />} />
            </Route>

            <Route path="/app" element={<Protected roles={["worker", "team_leader"]}><WorkerLayout /></Protected>}>
              <Route index element={<WorkerHome />} />
              <Route path="tasks" element={<WorkerTasks />} />
              <Route path="leads" element={<WorkerLeads />} />
              <Route path="performance" element={<WorkerPerformance />} />
              <Route path="profile" element={<WorkerProfile />} />
            </Route>

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}

export default App;
